import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Clock, FileText, Loader2, MessagesSquare, RefreshCw, UserMinus, UserRound } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import { classNames, formatDateTime } from '@/lib/utils';
import type { Conversacion, HiloWhatsApp } from '@/lib/comunicaciones';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { MensajesWhatsApp, RedactarWhatsApp } from '@/components/comunicaciones/HiloWhatsApp';
import ElegirPlantilla from '@/components/comunicaciones/ElegirPlantilla';
import Modal from '@/components/ui/Modal';

/**
 * Las respuestas por WhatsApp a las campañas y las conversaciones con leads
 * empezadas desde su ficha. Lo que escriben los huéspedes por sus reservas lo
 * atiende Hostify y no pasa por aquí.
 *
 * Se puede contestar con texto libre durante las 24 h siguientes al último
 * mensaje de la persona; después, WhatsApp solo admite plantillas.
 */
export default function ConversacionesPage() {
  const [params, setParams] = useSearchParams();
  const elegido = params.get('t') || '';
  const lista = useApi(() => api.getConversacionesWhatsApp(), []);
  const [soloPendientes, setSoloPendientes] = useState(false);

  // Se refresca sola cada 30 s: es una bandeja, la gente contesta mientras la miras
  useEffect(() => {
    const t = setInterval(lista.refresh, 30_000);
    return () => clearInterval(t);
  }, [lista.refresh]);

  const conversaciones = useMemo(
    () => (lista.data || []).filter(c => !soloPendientes || c.sinLeer > 0 || !c.atendida),
    [lista.data, soloPendientes],
  );
  const abrir = (t: string) => setParams(t ? { t } : {});
  /** Algo ha cambiado en una conversación: la lista y el número del menú, al día. */
  const cambio = () => {
    lista.refresh();
    window.dispatchEvent(new Event('sereno:contadores'));
  };

  // A todo el ancho y al alto de la ventana: es una bandeja de chat, el hilo se desplaza dentro
  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <MessagesSquare size={24} className="text-sereno-dark" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Conversaciones</h1>
            <p className="text-sm text-sereno-dark/40 mt-0.5">Respuestas por WhatsApp a las campañas y conversaciones con leads.</p>
          </div>
        </div>
        <button onClick={lista.refresh} className="btn-ghost text-sm flex items-center gap-2">
          {lista.loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          <span className="hidden sm:inline">Actualizar</span>
        </button>
      </div>

      {lista.error && <div className="card text-sm text-red-600 mb-4">{lista.error}</div>}

      {lista.loading && !lista.data ? <LoadingSpinner /> : (lista.data || []).length === 0 ? (
        <EmptyState title="Nadie ha contestado todavía" description="Cuando alguien responda a una campaña de WhatsApp, o le escribáis a un lead desde su ficha, aparecerá aquí." />
      ) : (
        <div className="card p-0 overflow-hidden grid grid-cols-1 md:grid-cols-[320px_1fr] xl:grid-cols-[380px_1fr] md:h-[calc(100dvh-9.5rem)] lg:h-[calc(100dvh-8rem)] md:min-h-[480px]">
          <div className={classNames('border-r border-gray-100 flex flex-col md:min-h-0', elegido && 'hidden md:flex')}>
            <label className="flex items-center gap-2 text-xs text-sereno-dark/60 px-4 py-2.5 border-b border-gray-100">
              <input type="checkbox" checked={soloPendientes} onChange={e => setSoloPendientes(e.target.checked)} /> Solo pendientes
            </label>
            <div className="overflow-auto flex-1 divide-y divide-gray-100">
              {conversaciones.map(c => (
                <button key={c.telefono} type="button" onClick={() => abrir(c.telefono)}
                  className={classNames('w-full text-left px-4 py-3 hover:bg-slate-50/70', c.telefono === elegido && 'bg-slate-50')}>
                  <div className="flex items-center gap-2">
                    <span className={classNames('flex-1 truncate', c.sinLeer > 0 ? 'font-semibold text-sereno-dark' : 'text-sereno-dark/80')}>{c.nombre}</span>
                    <span className="text-[11px] text-sereno-dark/40 whitespace-nowrap">{formatDateTime(c.ultimoMensajeEn)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-sereno-dark/50 truncate flex-1">{c.ultimoTexto}</span>
                    {c.leadId && <span className="badge bg-sky-100 text-sky-700">lead</span>}
                    {c.baja && <UserMinus size={12} className="text-sereno-dark/40" aria-label="Se dio de baja" />}
                    {c.sinLeer > 0 && <span className="badge bg-emerald-500 text-white">{c.sinLeer}</span>}
                  </div>
                </button>
              ))}
              {conversaciones.length === 0 && <p className="px-4 py-6 text-sm text-sereno-dark/40 text-center">No hay pendientes.</p>}
            </div>
          </div>
          <div className={classNames('md:min-h-0 md:h-full', !elegido && 'hidden md:block')}>
            {elegido
              ? <Hilo telefono={elegido} onVolver={() => abrir('')} onCambio={cambio} />
              : <div className="h-full flex items-center justify-center text-sm text-sereno-dark/40">Elige una conversación</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function Hilo({ telefono, onVolver, onCambio }: { telefono: string; onVolver: () => void; onCambio: () => void }) {
  const [hilo, setHilo] = useState<HiloWhatsApp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [eligiendo, setEligiendo] = useState(false);

  const cargar = async (marcar = false) => {
    try {
      const h = await api.getConversacionWhatsApp(telefono);
      setHilo(h);
      setError(null);
      if (marcar && h.conversacion.sinLeer > 0) {
        await api.marcarLeidaWhatsApp(telefono);
        onCambio();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar');
    }
  };

  useEffect(() => {
    setHilo(null);
    void cargar(true);
    const t = setInterval(() => { void cargar(true); }, 15_000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telefono]);

  const atendida = async (v: boolean) => {
    await api.marcarAtendidaWhatsApp(telefono, v);
    await cargar();
    onCambio();
  };

  if (!hilo) {
    return <div className="p-6">{error ? <div className="text-sm text-red-600">{error}</div> : <LoadingSpinner />}</div>;
  }
  const c: Conversacion = hilo.conversacion;

  return (
    <div className="flex flex-col h-full max-h-[720px] md:max-h-none">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <button type="button" onClick={onVolver} className="md:hidden p-1 -ml-1 text-sereno-dark/50"><ArrowLeft size={18} /></button>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sereno-dark truncate">{c.nombre}</div>
          <div className="text-xs text-sereno-dark/40 truncate">
            {c.telefono}{c.email ? ` · ${c.email}` : ''}{c.campanaNombre ? ` · campaña «${c.campanaNombre}»` : ''}
          </div>
        </div>
        {c.leadId && (
          <Link to={`/leads/propietarios?lead=${encodeURIComponent(c.leadId)}`} className="btn-ghost border border-gray-200 text-xs px-2.5 py-1 flex items-center gap-1" title="Abrir la ficha del lead">
            <UserRound size={13} /> Lead
          </Link>
        )}
        <button type="button" onClick={() => atendida(!c.atendida)} className={classNames('btn-ghost border text-xs px-2.5 py-1 flex items-center gap-1', c.atendida ? 'border-emerald-200 text-emerald-700' : 'border-gray-200')}>
          <Check size={13} /> {c.atendida ? 'Atendida' : 'Marcar atendida'}
        </button>
      </div>

      {c.baja && (
        <div className="px-4 py-2 text-xs bg-amber-50 text-amber-800 flex items-center gap-1.5">
          <UserMinus size={13} /> Pidió no recibir más mensajes por WhatsApp el {formatDateTime(c.baja)}.
        </div>
      )}

      <MensajesWhatsApp mensajes={hilo.mensajes} className="flex-1 min-h-0" anchoBurbuja="max-w-[80%] lg:max-w-[65%]" />

      <div className="border-t border-gray-100 p-3 space-y-2">
        {error && <div className="text-xs text-red-600">{error}</div>}
        {c.baja ? (
          <div className="text-xs text-sereno-dark/50">No se le puede escribir: pidió no recibir más mensajes por WhatsApp.</div>
        ) : hilo.ventana.abierta ? (
          <>
            <RedactarWhatsApp telefono={telefono} hasta={hilo.ventana.hasta} onEnviado={async () => { await cargar(); onCambio(); }} />
            <button type="button" onClick={() => setEligiendo(true)} className="text-xs text-sereno-dark/60 hover:text-sereno-dark flex items-center gap-1">
              <FileText size={13} /> Enviar una plantilla
            </button>
          </>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-sereno-dark/50 flex items-center gap-1.5 flex-1 min-w-[220px]">
              <Clock size={13} className="shrink-0" /> Han pasado más de 24 h desde su último mensaje: WhatsApp solo deja escribirle con una plantilla.
            </span>
            <button type="button" onClick={() => setEligiendo(true)} className="btn-primary text-sm flex items-center gap-1.5">
              <FileText size={14} /> Elegir plantilla
            </button>
          </div>
        )}
      </div>

      <Modal isOpen={eligiendo} onClose={() => setEligiendo(false)} title={`Enviar una plantilla a ${c.nombre}`} size="xl">
        {eligiendo && (
          <PlantillasParaConversacion
            conversacion={c}
            onEnviada={async () => { setEligiendo(false); await cargar(); onCambio(); }}
          />
        )}
      </Modal>
    </div>
  );
}

/**
 * Las plantillas que se pueden mandar desde aquí: las del CRM aprobadas y sin
 * imagen (las de Hostify son suyas). A un lead, las suyas primero.
 */
function PlantillasParaConversacion({ conversacion: c, onEnviada }: { conversacion: Conversacion; onEnviada: () => Promise<void> }) {
  const plantillas = useApi(() => api.getPlantillasWhatsApp(), []);
  const disponibles = useMemo(() => {
    // La del portal lleva el enlace para crear la contraseña: solo la manda la ficha del propietario
    const ok = (plantillas.data || []).filter(p => p.delCrm && p.uso !== 'portal' && p.aprobada && !p.huecos.some(h => h.imagen));
    const primero = c.leadId ? 'leads' : 'campanas';
    return [...ok].sort((a, b) => Number(b.uso === primero) - Number(a.uso === primero));
  }, [plantillas.data, c.leadId]);
  // Nombre de pila para {{1}}: el del lead, o el de la conversación si no es un teléfono
  const nombrePila = (c.leadNombre || (/^\+?\d/.test(c.nombre) ? '' : c.nombre)).split(/\s+/)[0] || '';

  if (plantillas.loading && !plantillas.data) return <LoadingSpinner />;
  if (plantillas.error) return <p className="text-sm text-red-600">No se pudieron leer las plantillas: {plantillas.error}</p>;
  if (!disponibles.length) {
    return (
      <p className="text-sm text-sereno-dark/60">
        No hay plantillas del CRM aprobadas. <Link to="/comunicaciones/plantillas" className="underline">Créalas en Comunicaciones → Plantillas</Link>.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      <p className="text-xs text-sereno-dark/50">Marca una y mira cómo le llegará. Se manda tal cual la aprobó Meta; vale también pasadas 24 h desde su último mensaje.</p>
      <ElegirPlantilla
        ancho
        disponibles={disponibles}
        nombrePila={nombrePila}
        idioma={c.idioma}
        onEnviar={async (plantilla, parametros) => { await api.enviarPlantillaAConversacion(c.telefono, plantilla, parametros); await onEnviada(); }}
      />
    </div>
  );
}
